// src/pages/pentest/dashboard/Exploits.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useNavigate, useParams } from 'react-router-dom';

import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';
import ExploitsModal from './exploitsModal';

// ---- helpers ----
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info', 'informational'];

const sevColor = (sev) => {
  const s = String(sev || '').toLowerCase();
  switch (s) {
    case 'critical': return '#9c27b0';
    case 'high': return '#f44336';
    case 'medium': return '#ff9800';
    case 'low': return '#2196f3';
    case 'info':
    case 'informational': return '#4caf50';
    default: return '#9e9e9e';
  }
};

// Robustly parse impacted_assets whether it's:
// - array: ["a", "b"]
// - comma separated: "a, b"
// - python repr list string: "['a', 'b']"
// - mixed "a and b"
const parseImpactedAssets = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return [...new Set(val.map((x) => String(x).trim()).filter(Boolean))];
  }

  const str = String(val).trim();
  if (!str) return [];

  // Handle stringified arrays: JSON or python-like
  const looksBracketed =
    (str.startsWith('[') && str.endsWith(']')) ||
    (str.startsWith('(') && str.endsWith(')'));

  if (looksBracketed) {
    // Try JSON first
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return [...new Set(parsed.map((x) => String(x).trim()).filter(Boolean))];
      }
    } catch {}

    // Try extracting quoted strings (handles "['a','b']" or '["a","b"]')
    const quoted = [];
    const re = /'([^']*)'|"([^"]*)"/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      const hit = (m[1] ?? m[2] ?? '').trim();
      if (hit) quoted.push(hit);
    }
    if (quoted.length) return [...new Set(quoted)];

    // Fallback: strip brackets and split
    const inner = str.replace(/^[\[\(]\s*/, '').replace(/\s*[\]\)]$/, '');
    return [...new Set(
      inner
        .split(/,|\s+and\s+/gi)
        .map((s) => s.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean)
    )];
  }

  // Non-bracketed: split by comma / semicolon / pipe / " and "
  return [...new Set(
    str
      .split(/[;|]|,|\s+and\s+/gi)
      .map((s) => s.trim())
      .filter(Boolean)
  )];
};

// --- mini row bar like API page ---
const NiceBarRow = ({ label, value = 0, max = 1, onClick }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <Stack spacing={0.5} sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={label}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {value}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 999 }} />
    </Stack>
  );
};

const Exploits = () => {
  const [toolsData, setToolsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('any'); // any | finding | severity | description | location
  const [selectedExploit, setSelectedExploit] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('report'); // report | logs

  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const getTools = (token) => {
    fetch(`${BASEURL}/pentest/exploits?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') navigate('/');
        else setToolsData(Array.isArray(data?.exploits) ? data.exploits : []);
      })
      .catch((err) => console.error('Error fetching data:', err));
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getTools(token);
    else navigate('/');
  }, [navigate, pentest_id]);

  // ---- enrich rows ----
  const rows = useMemo(() => {
    return toolsData.map((row, i) => {
      const sev = String(row.severity || '').toLowerCase();
      const locations = parseImpactedAssets(row.impacted_assets);
      return {
        id: row.id ?? `${i}-${row.name || ''}`,
        severity: sev,
        finding: row.name || '',
        description: row.description || '',
        locations,
        locationText: locations.join(', '),
        raw: row,
      };
    });
  }, [toolsData]);

  // ---- KPIs ----
  const sevCounts = useMemo(() => {
    const out = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    rows.forEach((r) => {
      const s = (r.severity || '').toLowerCase();
      if (s === 'info' || s === 'informational') out.info++;
      else if (out[s] !== undefined) out[s]++;
    });
    return out;
  }, [rows]);

  const topLocations = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => r.locations.forEach((a) => map.set(a, (map.get(a) || 0) + 1)));
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [rows]);

  const topLocations5 = useMemo(
    () => topLocations.slice(0, 5).map(([k, v]) => ({ key: k, value: v })),
    [topLocations]
  );

  const countByFinding = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r.finding || '—';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([k, v]) => ({ key: k, value: v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);

  const maxLocationCount = Math.max(1, ...topLocations5.map((x) => x.value));
  const maxFindingCount = Math.max(1, ...countByFinding.map((x) => x.value));

  // ---- filter + sort ----
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();

    return rows.filter((r) => {
      const finding = (r.finding || '').toLowerCase();
      const severity = (r.severity || '').toLowerCase();
      const desc = (r.description || '').toLowerCase();
      const loc = (r.locationText || '').toLowerCase();

      if (searchCategory === 'finding') return finding.includes(q);
      if (searchCategory === 'severity') return severity.includes(q);
      if (searchCategory === 'description') return desc.includes(q);
      if (searchCategory === 'location') return loc.includes(q);

      return finding.includes(q) || severity.includes(q) || desc.includes(q) || loc.includes(q);
    });
  }, [rows, searchTerm, searchCategory]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort(
      (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
    );
  }, [filteredRows]);

  // ---- side drawer actions ----
  const openDrawer = (exploit, mode) => {
    setSelectedExploit(exploit);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedExploit(null);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
    } catch {}
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* ---------- Header ---------- */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Exploits
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Found exploits with report details (markdown) + agent logs (suggestion/commands/observation).
              </Typography>
            </Box>
          </Stack>

          {/* Severity cards */}
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {['critical', 'high', 'medium', 'low', 'info'].map((s) => (
              <Card key={s} sx={{ p: 2, borderRadius: 3, boxShadow: 2, minWidth: 180 }}>
                <Typography variant="overline" color="text.secondary">
                  {s.toUpperCase()}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" sx={{ bgcolor: sevColor(s), color: '#fff', fontWeight: 700 }} label={s} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {sevCounts[s]}
                  </Typography>
                </Stack>
              </Card>
            ))}
          </Stack>

          {/* Mini charts (progress rows) */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                TOP 5 LOCATIONS
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {topLocations5.length ? (
                  topLocations5.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxLocationCount}
                      onClick={() => {
                        setSearchCategory('location');
                        setSearchTerm(row.key);
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </Stack>
            </Card>

            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                TOP 5 FINDINGS
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {countByFinding.length ? (
                  countByFinding.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxFindingCount}
                      onClick={() => {
                        setSearchCategory('finding');
                        setSearchTerm(row.key);
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </Stack>
            </Card>
          </Stack>

          <Divider />
        </Stack>

        {/* ---------- Filters ---------- */}
        <Card sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Exploit Inventory
              </Typography>
              <Chip
                size="small"
                label={`${sortedRows.length}/${rows.length || 0} shown`}
                sx={{ ml: 1, fontWeight: 700 }}
                variant="outlined"
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter By</InputLabel>
                <Select value={searchCategory} label="Filter By" onChange={(e) => setSearchCategory(e.target.value)}>
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="finding">Finding</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="description">Description</MenuItem>
                  <MenuItem value="location">Location</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search… (e.g., XSS, high, rest.vulnweb.com)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ ml: 1, minWidth: 320 }}
              />
              <Button variant="outlined" onClick={() => setSearchTerm('')}>
                Clear
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* ---------- Table ---------- */}
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 6, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead sx={{ backgroundColor: PRIMARYCOLOR }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 130 }}>Severity</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 360 }}>Finding</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 460 }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 320 }}>Location</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 140 }}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length ? (
                  sortedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ '&:hover': { backgroundColor: 'action.hover' }, verticalAlign: 'top' }}
                    >
                      {/* Severity */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.severity ? row.severity.toUpperCase() : '—'}
                          sx={{ bgcolor: sevColor(row.severity), color: '#fff', fontWeight: 800 }}
                        />
                      </TableCell>

                      {/* Finding */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Tooltip title={row.finding || ''} placement="top" arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontWeight: 700,
                                lineHeight: 1.35,
                              }}
                            >
                              {row.finding || '—'}
                            </Typography>
                          </Tooltip>

                          {row.finding ? (
                            <Tooltip title="Copy finding" arrow>
                              <IconButton size="small" onClick={() => copy(row.finding)} sx={{ mt: '-2px' }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>

                      {/* Description (from exploit.description) */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Tooltip title={row.description || ''} placement="top" arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.35,
                              }}
                            >
                              {row.description || '—'}
                            </Typography>
                          </Tooltip>

                          {row.description ? (
                            <Tooltip title="Copy description" arrow>
                              <IconButton size="small" onClick={() => copy(row.description)} sx={{ mt: '-2px' }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>

                      {/* Location (from impacted_assets, fixed so no "[' ... ']" strings) */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {row.locations.length ? (
                            row.locations.map((a) => (
                              <Chip key={a} label={a} size="small" sx={{ maxWidth: 260 }} />
                            ))
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Actions: report + logs */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Open report (Markdown)" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openDrawer(row.raw, 'report')}
                                disabled={!row.raw?.report_details}
                              >
                                <DescriptionOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Open agent logs (Suggestion / Commands / Observation)" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openDrawer(row.raw, 'logs')}
                                disabled={!row.raw?.command_output}
                              >
                                <TerminalIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography>No data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Side overlay (right drawer) */}
      <ExploitsModal
        open={drawerOpen}
        handleClose={closeDrawer}
        exploit={selectedExploit}
        mode={drawerMode}
      />
    </Box>
  );
};

export default Exploits;
